import { Loader2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RiskLevelBadge } from "@/components/ui/risk-level-badge/RiskLevelBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { useRunTargetareRisc } from "@/hooks/api/mutate/useRunTargetareRisc";
import { useState } from "react";
import { useGetTargetareStats } from "@/hooks/api/query/useGetTargetareStats";
import { useGetTargetareUniversComplet } from "@/hooks/api/query/useGetTargetareUniversComplet";
import type { TargetareFiltre } from "@/types/targetare";
import { definitieRaportRisc } from "@/utils/targetareRaportIncadrare";
import { FisaFirmaDialog } from "../components/FisaFirmaDialog";
import { celulaFirma } from "../components/raport/CelulaFirma";
import { RaportInteractiv } from "../components/raport/RaportInteractiv";
import { useToast } from "@/hooks/use-toast";
import { formatDataOra, formatNumar } from "@/utils/targetareFormatting";
import { jobActiv } from "@/utils/targetareJoburi";
import { riskLevelDinNivel } from "@/utils/targetareRisc";
import { ETICHETE_NIVEL_RISC } from "@/utils/targetareTabel";

const NIVELURI = ["esential", "important", "de_baza", "neevaluat"] as const;
const MARIMI = ["S", "M", "L"] as const;

const FILTRE_SCOP: TargetareFiltre = { doar_scop_dimensiune: true };

export default function RiscTargetare() {
  const [cuiSelectat, setCuiSelectat] = useState<string | null>(null);
  const { toast } = useToast();
  const stats = useGetTargetareStats();
  const univers = useGetTargetareUniversComplet(
    FILTRE_SCOP,
    stats.data?.ultima_clasificare
  );
  const risc = useRunTargetareRisc();
  const jobInCurs = jobActiv(stats.data?.joburi ?? []);
  const distributie = stats.data?.pe_nivel_risc_scop ?? {};
  const scoruri = stats.data?.risc_sectoare ?? {};
  const sectoare = stats.data?.sectoare ?? [];

  const ruleaza = () =>
    risc.mutate(undefined, {
      onSuccess: (r) =>
        toast({
          title: "Estimare programată",
          description: `Job #${r.data.job_id} rulează în fundal; durează sub un minut.`,
        }),
      onError: (e) =>
        toast({
          title: "Estimarea a eșuat",
          description: e.message,
          variant: "destructive",
        }),
    });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h2 className="text-xl font-heading font-semibold">
            Nivelul de risc estimat
          </h2>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Metodologia ENIRE / CyberFundamentals din analiza de risc a
            aplicației: pentru fiecare sector, matricea actori × categorii de
            atac dă un scor = probabilitate (0 / 0,5 / 1) × impact (0 / 5 / 10)
            × tip de atac (global 1, țintit 2) × mărimea organizației (S 1, M 2,
            L 3). Sub 100 „de bază”, sub 200 „important”, de la 200 „esențial”.
            Estimarea în masă folosește matricea implicită a fiecărui sector,
            sectoarele firmei (CAEN principal și liste) și dimensiunea din
            bilanț; ca în aplicație, scorul firmei este maximul dintre
            sectoarele ei.
          </p>
        </div>
        <Button onClick={ruleaza} disabled={risc.isPending || !!jobInCurs}>
          {risc.isPending || jobInCurs ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <ShieldAlert className="mr-2 h-4 w-4" />
          )}
          Estimează nivelul de risc
        </Button>
      </div>

      {jobInCurs && (
        <p className="rounded-md border border-ds-blue-200 bg-ds-blue-50 p-3 text-sm text-ds-blue-700">
          Job #{jobInCurs.id} ({jobInCurs.tip}) rulează în fundal.
        </p>
      )}

      {stats.isLoading ? (
        <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
            {NIVELURI.map((n) => (
              <Card key={n}>
                <CardContent className="space-y-1 p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                      {ETICHETE_NIVEL_RISC[n] ?? n}
                    </p>
                    <RiskLevelBadge riskLevel={riskLevelDinNivel(n)} />
                  </div>
                  <p className="text-2xl font-heading font-semibold tabular-nums">
                    {formatNumar(distributie[n] ?? 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {n === "neevaluat"
                      ? "fără sector atribuit (doar coduri secundare)"
                      : "firme în scopul de dimensiune"}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Ultima estimare:{" "}
            {formatDataOra(stats.data?.risc_calculat_la ?? null)}. Nivelul se
            recalculează automat după fiecare rulare a încadrării.
          </p>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Scorul implicit pe sector și mărimea organizației
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-4">Sector</th>
                  {MARIMI.map((m) => (
                    <th key={m} className="py-2 pr-4">
                      {m === "S" ? "Mică" : m === "M" ? "Mijlocie" : "Mare"}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sectoare.map((s) => (
                  <tr key={s.sector_id} className="border-b last:border-0">
                    <td className="py-1.5 pr-4">
                      {s.nume}{" "}
                      <span className="text-xs text-muted-foreground">
                        (anexa {s.anexa ?? "–"})
                      </span>
                    </td>
                    {MARIMI.map((m) => {
                      const scor = scoruri[`${s.sector_id}:${m}`];
                      return (
                        <td key={m} className="py-1.5 pr-4 tabular-nums">
                          {scor === undefined ? (
                            <span className="text-muted-foreground">
                              fără matrice
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-2">
                              {formatNumar(scor)}
                              <RiskLevelBadge
                                riskLevel={riskLevelDinNivel(
                                  scor < 100
                                    ? "de_baza"
                                    : scor < 200
                                      ? "important"
                                      : "esential"
                                )}
                              />
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <section className="space-y-2">
        <h3 className="text-base font-heading font-semibold">
          Firmele și nivelul lor de risc
        </h3>
        <p className="max-w-3xl text-sm text-muted-foreground">
          Coloana „Risc pe sectoare” arată scorul fiecărui sector al firmei
          (mărimea ei); primul, marcat cu ▲, este sectorul care dă nivelul final
          (agregare „maxim”, ca în aplicație). Click pe rând deschide fișa.
        </p>
        <RaportInteractiv
          definitie={definitieRaportRisc}
          randuri={univers.data ?? []}
          isLoading={univers.isLoading}
          selectat={cuiSelectat}
          onSelect={(cui) => setCuiSelectat(cui)}
          celula={celulaFirma}
          claseRand={(r) => (r.insolventa ? "opacity-70" : undefined)}
        />
      </section>
      <FisaFirmaDialog cui={cuiSelectat} onClose={() => setCuiSelectat(null)} />
    </div>
  );
}
